import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ddtApi, paymentsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Search, Eye, Download, Trash2, FileText, Coins, FileArchive, Trash, PackagePlus, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { toast } from 'sonner';

const PAGE_SIZE = 20;

const DDTListPage = () => {
  const { isSlave } = useAuth();
  const canBulkDelete = !isSlave();
  const [ddtList, setDdtList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1); // 1-based
  const [totalCount, setTotalCount] = useState(0);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkPwd, setBulkPwd] = useState('');
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDDT, setSelectedDDT] = useState(null);
  const [deletePwd, setDeletePwd] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Export dialog
  const currentYear = new Date().getFullYear();
  const [exportDialog, setExportDialog] = useState(false);
  const [exportYear, setExportYear] = useState(currentYear);
  const [exportMonth, setExportMonth] = useState('all'); // 'all' | 1..12
  const [exportLoading, setExportLoading] = useState(false);

  // Payment dialog (quick acconto su DDT)
  const [paymentDDT, setPaymentDDT] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ amount: '', method: 'Contanti', payment_date: new Date().toISOString().slice(0, 10), note: '' });
  const [paymentSaving, setPaymentSaving] = useState(false);

  const openPaymentDialog = (ddt) => {
    const vecchio = ddt.previous_balance || 0;
    const resto = ((ddt.total_amount || 0) + vecchio) - (ddt.paid_amount || 0);
    setPaymentForm({
      amount: resto > 0 ? resto.toFixed(2) : '',
      method: 'Contanti',
      payment_date: new Date().toISOString().slice(0, 10),
      note: '',
    });
    setPaymentDDT(ddt);
  };

  const submitPayment = async (e) => {
    e.preventDefault();
    if (!paymentDDT) return;
    const amount = parseFloat(paymentForm.amount);
    if (!amount || amount <= 0) { toast.error('Importo non valido'); return; }
    setPaymentSaving(true);
    try {
      await paymentsApi.create({
        ddt_id: paymentDDT.id,
        client_id: paymentDDT.client_id || '',
        client_name: paymentDDT.client_name || '',
        amount,
        method: paymentForm.method,
        payment_date: paymentForm.payment_date,
        note: paymentForm.note,
      });
      toast.success(`Acconto di € ${amount.toFixed(2)} registrato su DDT ${paymentDDT.number}-${paymentDDT.year}`);
      setPaymentDDT(null);
      fetchDDTList(search, page);
    } catch (err) {
      toast.error('Errore registrazione acconto');
    } finally {
      setPaymentSaving(false);
    }
  };

  useEffect(() => {
    fetchDDTList('', 1);
  }, []);

  const fetchDDTList = async (searchTerm = search, targetPage = page) => {
    setLoading(true);
    try {
      const skip = (targetPage - 1) * PAGE_SIZE;
      const response = await ddtApi.getAll({ search: searchTerm, skip, limit: PAGE_SIZE });
      setDdtList(response.data);
      const total = parseInt(response.headers?.['x-total-count'] || '0', 10);
      setTotalCount(isNaN(total) ? 0 : total);
      setPage(targetPage);
    } catch (error) {
      toast.error('Errore nel caricamento dei DDT');
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const canPrev = page > 1;
  const canNext = page < totalPages;
  const goToPrev = () => { if (canPrev) fetchDDTList(search, page - 1); };
  const goToNext = () => { if (canNext) fetchDDTList(search, page + 1); };

  // ==== Selezione multipla (checkbox) ====
  // Reset selezione ogni volta che cambia la pagina o il filtro di ricerca
  useEffect(() => { setSelectedIds(new Set()); }, [page, search]);
  // I DDT selezionabili sono quelli non-bozza (le bozze non hanno un'azione elimina uguale)
  const selectableDdt = ddtList.filter(d => d.status !== 'draft');
  const allOnPageSelected = selectableDdt.length > 0 && selectableDdt.every(d => selectedIds.has(d.id));
  const someOnPageSelected = selectableDdt.some(d => selectedIds.has(d.id)) && !allOnPageSelected;

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAllOnPage = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        selectableDdt.forEach(d => next.delete(d.id));
      } else {
        selectableDdt.forEach(d => next.add(d.id));
      }
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkDelete = async () => {
    if ((bulkPwd || '').trim() !== '1997') {
      toast.error('Codice di sicurezza errato — nessun DDT è stato eliminato');
      return;
    }
    setBulkDeleting(true);
    try {
      const ids = Array.from(selectedIds);
      const r = await ddtApi.bulkDelete(ids, '1997');
      toast.success(`${r.data?.deleted ?? ids.length} DDT spostati nel cestino`);
      setBulkDialogOpen(false);
      setBulkPwd('');
      clearSelection();
      fetchDDTList(search, page);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nell\'eliminazione multipla');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
    // ricerca: ripartiamo dalla prima pagina
    fetchDDTList(value, 1);
  };

  const openDeleteDialog = (ddt) => {
    setSelectedDDT(ddt);
    setDeletePwd('');
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if ((deletePwd || '').trim() !== '1997') {
      toast.error('Codice di sicurezza errato — il DDT NON è stato eliminato');
      return;
    }
    setDeleting(true);
    try {
      await ddtApi.delete(selectedDDT.id, '1997');
      toast.success(`DDT ${selectedDDT.number}-${selectedDDT.year} spostato nel cestino`);
      setDeleteDialogOpen(false);
      setDeletePwd('');
      fetchDDTList(search, page);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    } finally {
      setDeleting(false);
    }
  };

  const handleDownloadPdf = async (ddt) => {
    try {
      const response = await ddtApi.getPdf(ddt.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `DDT_${ddt.number}_${ddt.year}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF scaricato');
    } catch (error) {
      toast.error('Errore nel download del PDF');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT');
  };

  const handleExportZip = async () => {
    setExportLoading(true);
    try {
      const month = exportMonth === 'all' ? null : parseInt(exportMonth);
      const r = await ddtApi.exportZip(parseInt(exportYear), month);
      const blob = new Blob([r.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = month ? `DDT_${exportYear}-${String(month).padStart(2,'0')}.zip` : `DDT_${exportYear}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Archivio scaricato');
      setExportDialog(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Nessun DDT da esportare per il periodo selezionato');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div data-testid="ddt-list-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight font-['Manrope']">Documenti di Trasporto</h1>
          <p className="text-muted-foreground text-sm">Elenco DDT emessi</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="open-export-zip-btn"
            variant="outline"
            onClick={() => setExportDialog(true)}
            title="Scarica DDT raggruppati"
          >
            <FileArchive className="w-4 h-4 mr-2" />
            SCARICA PERIODO
          </Button>
          <Link to="/cestino">
            <Button data-testid="open-trash-btn" variant="outline" title="Cestino DDT eliminati">
              <Trash className="w-4 h-4 mr-2" />
              CESTINO
            </Button>
          </Link>
          <Link to="/ddt/new">
            <Button data-testid="new-ddt-btn" className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              NUOVO DDT
            </Button>
          </Link>
        </div>
      </div>

      {/* Search */}
      <Card className="card-swiss">
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              data-testid="search-ddt-input"
              placeholder="Cerca per numero o cliente..."
              value={search}
              onChange={handleSearch}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Bulk actions bar — visibile quando ci sono DDT selezionati */}
      {canBulkDelete && selectedIds.size > 0 && (
        <div
          data-testid="bulk-actions-bar"
          className="flex items-center justify-between gap-3 rounded-md border border-destructive/40 bg-red-50 px-4 py-3"
        >
          <div className="text-sm">
            <span
              data-testid="bulk-selected-count"
              className="font-semibold text-red-800"
            >
              {selectedIds.size}
            </span>{' '}
            <span className="text-red-800/80">DDT selezionati</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="bulk-clear-btn"
              variant="ghost"
              size="sm"
              onClick={clearSelection}
              className="h-9 text-muted-foreground"
            >
              <X className="w-4 h-4 mr-1" />
              Deseleziona
            </Button>
            <Button
              data-testid="bulk-delete-btn"
              variant="destructive"
              size="sm"
              onClick={() => { setBulkPwd(''); setBulkDialogOpen(true); }}
              className="h-9"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Elimina selezionati
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <Card className="card-swiss">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-muted-foreground">Caricamento...</div>
            </div>
          ) : ddtList.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  {canBulkDelete && (
                    <TableHead className="table-header-swiss w-[40px]">
                      <Checkbox
                        data-testid="ddt-select-all"
                        checked={allOnPageSelected}
                        // indeterminate visual state se solo alcuni selezionati
                        data-state={someOnPageSelected ? 'indeterminate' : (allOnPageSelected ? 'checked' : 'unchecked')}
                        onCheckedChange={toggleSelectAllOnPage}
                        aria-label="Seleziona tutti i DDT in pagina"
                      />
                    </TableHead>
                  )}
                  <TableHead className="table-header-swiss w-[120px]">N° DDT</TableHead>
                  <TableHead className="table-header-swiss">Cliente</TableHead>
                  <TableHead className="table-header-swiss w-[110px]">Data</TableHead>
                  <TableHead className="table-header-swiss w-[120px] text-right">Totale</TableHead>
                  <TableHead className="table-header-swiss w-[100px] text-right">Vecchio</TableHead>
                  <TableHead className="table-header-swiss w-[110px] text-right">Pagato</TableHead>
                  <TableHead className="table-header-swiss w-[110px] text-right">Resto</TableHead>
                  <TableHead className="table-header-swiss w-[140px]">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ddtList.map((ddt) => {
                  const paid = ddt.paid_amount || 0;
                  const vecchio = ddt.previous_balance || 0;
                  const grandTotal = (ddt.total_amount || 0) + vecchio;
                  const rawResto = grandTotal - paid;
                  // Tolleranza ±0.01 € per evitare -0,00 € e arancione su DDT pagati per intero
                  const resto = Math.abs(rawResto) < 0.01 ? 0 : rawResto;
                  const isDraft = ddt.status === 'draft';
                  // is_paid_external: il backend ha verificato che il DDT è coperto
                  // da pagamenti generici (Movimenti) in FIFO.
                  const isPaid = (resto <= 0 && grandTotal > 0) || ddt.is_paid_external === true;
                  return (
                  <TableRow key={ddt.id} className={`table-row-swiss ${isDraft ? 'bg-amber-50/40' : ''} ${selectedIds.has(ddt.id) ? 'bg-red-50/60' : ''}`}>
                    {canBulkDelete && (
                      <TableCell className="table-cell-swiss">
                        {!isDraft && (
                          <Checkbox
                            data-testid={`select-ddt-${ddt.id}`}
                            checked={selectedIds.has(ddt.id)}
                            onCheckedChange={() => toggleSelect(ddt.id)}
                            aria-label={`Seleziona DDT ${ddt.number}-${ddt.year}`}
                          />
                        )}
                      </TableCell>
                    )}
                    <TableCell className="table-cell-swiss font-['JetBrains_Mono'] font-medium">
                      <div className="flex items-center gap-2">
                        <span>{ddt.number}-{ddt.year}</span>
                        {isDraft && (
                          <span
                            data-testid={`draft-badge-${ddt.id}`}
                            className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded-sm bg-amber-200 text-amber-900 font-['Manrope']"
                          >
                            BOZZA
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="table-cell-swiss">{ddt.client_name || <span className="text-muted-foreground italic">— non selezionato —</span>}</TableCell>
                    <TableCell className="table-cell-swiss">{formatDate(ddt.date)}</TableCell>
                    <TableCell className="table-cell-swiss text-right font-['JetBrains_Mono']">
                      {formatCurrency(ddt.total_amount)}
                    </TableCell>
                    <TableCell className="table-cell-swiss text-right font-['JetBrains_Mono']">
                      {vecchio > 0 ? (
                        <span data-testid={`vecchio-${ddt.id}`} className="inline-block px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800 font-semibold">
                          {formatCurrency(vecchio)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground/40">—</span>
                      )}
                    </TableCell>
                    <TableCell className="table-cell-swiss text-right font-['JetBrains_Mono'] text-green-700">
                      {formatCurrency(paid)}
                    </TableCell>
                    <TableCell className={`table-cell-swiss text-right font-['JetBrains_Mono'] font-semibold ${isPaid ? 'text-green-600' : 'text-orange-600'}`}>
                      {isPaid ? '✓ Pagato' : formatCurrency(resto)}
                    </TableCell>
                    <TableCell className="table-cell-swiss">
                      <div className="flex gap-1">
                        <Link to={isDraft ? '/ddt/new' : `/ddt/${ddt.id}`}>
                          <Button
                            data-testid={`view-ddt-${ddt.id}`}
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            title={isDraft ? 'Riprendi bozza' : 'Visualizza DDT'}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </Link>
                        {!isDraft && !isPaid && resto > 0 && (
                          <Button
                            data-testid={`pay-ddt-${ddt.id}`}
                            variant="ghost"
                            size="icon"
                            onClick={() => openPaymentDialog(ddt)}
                            title="Aggiungi acconto"
                            className="h-8 w-8 text-green-700 hover:bg-green-50"
                          >
                            <Coins className="w-4 h-4" />
                          </Button>
                        )}
                        {!isDraft && (
                          <Link to={`/ddt/${ddt.id}/edit?focus=add`}>
                            <Button
                              data-testid={`add-order-ddt-${ddt.id}`}
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-amber-700 hover:bg-amber-50"
                              title="Aggiungi un nuovo ordine a questo DDT"
                            >
                              <PackagePlus className="w-4 h-4" />
                            </Button>
                          </Link>
                        )}
                        {!isDraft && (
                          <Button data-testid={`download-ddt-${ddt.id}`} variant="ghost" size="icon" onClick={() => handleDownloadPdf(ddt)} className="h-8 w-8" title="Scarica PDF">
                            <Download className="w-4 h-4" />
                          </Button>
                        )}
                        {/* Separatore visivo + pulsante elimina spostato a destra: */}
                        <div className="ml-auto pl-2 border-l border-border/60">
                          <Button
                            data-testid={`delete-ddt-${ddt.id}`}
                            variant="ghost"
                            size="icon"
                            onClick={() => openDeleteDialog(ddt)}
                            className="h-8 w-8 text-destructive/40 hover:text-destructive hover:bg-red-50"
                            title="Elimina DDT (richiede codice 1997)"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                );})}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nessun DDT trovato</p>
              <Link to="/ddt/new">
                <Button className="mt-4 btn-primary">
                  <Plus className="w-4 h-4 mr-2" />
                  CREA IL PRIMO DDT
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination controls — mostrate solo se ci sono più DDT del limite pagina */}
      {!loading && totalCount > PAGE_SIZE && (
        <div
          data-testid="ddt-pagination"
          className="flex items-center justify-between gap-3 px-1"
        >
          <div className="text-xs text-muted-foreground">
            Pagina <span className="font-semibold text-foreground">{page}</span> di{' '}
            <span className="font-semibold text-foreground">{totalPages}</span>
            {' · '}
            <span>{totalCount} DDT totali</span>
          </div>
          <div className="flex items-center gap-1">
            <Button
              data-testid="ddt-page-prev"
              variant="outline"
              size="sm"
              onClick={goToPrev}
              disabled={!canPrev}
              className="h-9"
              aria-label="Pagina precedente"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Più recenti
            </Button>
            <Button
              data-testid="ddt-page-next"
              variant="outline"
              size="sm"
              onClick={goToNext}
              disabled={!canNext}
              className="h-9"
              aria-label="Pagina successiva (DDT più vecchi)"
            >
              Più vecchi
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Delete Confirmation con codice di sicurezza */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={(o) => { setDeleteDialogOpen(o); if (!o) setDeletePwd(''); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" />
              Eliminare il DDT n° {selectedDDT?.number}-{selectedDDT?.year}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Il DDT verrà spostato nel <b>Cestino</b> e potrà essere ripristinato da lì.
              Per evitare cancellazioni accidentali, inserisci il <b>codice di sicurezza</b>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">
              Codice di sicurezza
            </Label>
            <Input
              data-testid="delete-pwd-input"
              type="password"
              autoFocus
              value={deletePwd}
              onChange={(e) => setDeletePwd(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleDelete(); } }}
              placeholder="Inserisci codice"
              className="font-['JetBrains_Mono'] text-center text-lg tracking-widest"
              maxLength={10}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-ddt-btn"
              onClick={handleDelete}
              disabled={deleting || !deletePwd}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
            >
              {deleting ? 'Eliminazione…' : 'Elimina'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation dialog */}
      <AlertDialog open={bulkDialogOpen} onOpenChange={(o) => { setBulkDialogOpen(o); if (!o) setBulkPwd(''); }}>
        <AlertDialogContent data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" />
              Eliminare {selectedIds.size} DDT selezionati?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Tutti i DDT selezionati verranno spostati nel <b>Cestino</b> e potranno essere ripristinati singolarmente da lì.
              Per confermare l&apos;eliminazione multipla inserisci il <b>codice di sicurezza</b>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">
              Codice di sicurezza
            </Label>
            <Input
              data-testid="bulk-delete-pwd-input"
              type="password"
              autoFocus
              value={bulkPwd}
              onChange={(e) => setBulkPwd(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleBulkDelete(); } }}
              placeholder="Inserisci codice"
              className="font-['JetBrains_Mono'] text-center text-lg tracking-widest"
              maxLength={10}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-cancel-btn">Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="bulk-confirm-delete-btn"
              onClick={handleBulkDelete}
              disabled={bulkDeleting || !bulkPwd}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
            >
              {bulkDeleting ? 'Eliminazione…' : `Elimina ${selectedIds.size} DDT`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Quick Payment Dialog — registra acconto direttamente dalla riga DDT */}
      <Dialog open={!!paymentDDT} onOpenChange={(o) => !o && setPaymentDDT(null)}>
        <DialogContent data-testid="quick-payment-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Coins className="w-5 h-5 text-green-700" />
              Aggiungi Acconto
            </DialogTitle>
          </DialogHeader>
          {paymentDDT && (
            <form onSubmit={submitPayment} className="space-y-3">
              <div className="rounded-sm border border-border p-3 bg-muted/40 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">DDT</span><span className="font-['JetBrains_Mono'] font-semibold">{paymentDDT.number}-{paymentDDT.year}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Cliente</span><span className="font-semibold">{paymentDDT.client_name || '—'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Totale prodotti</span><span className="font-['JetBrains_Mono']">{formatCurrency(paymentDDT.total_amount)}</span></div>
                {(paymentDDT.previous_balance || 0) > 0 && (
                  <div className="flex justify-between"><span className="text-amber-700 font-semibold">+ VECCHIO</span><span className="font-['JetBrains_Mono'] font-semibold text-amber-700">{formatCurrency(paymentDDT.previous_balance)}</span></div>
                )}
                <div className="flex justify-between"><span className="text-muted-foreground">Già pagato</span><span className="font-['JetBrains_Mono'] text-green-700">{formatCurrency(paymentDDT.paid_amount || 0)}</span></div>
                <div className="flex justify-between border-t border-border pt-1 mt-1"><span className="font-semibold">Resto</span><span className="font-['JetBrains_Mono'] font-bold text-orange-600">{formatCurrency(Math.max(0, ((paymentDDT.total_amount || 0) + (paymentDDT.previous_balance || 0)) - (paymentDDT.paid_amount || 0)))}</span></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Importo (€)</Label>
                  <Input
                    data-testid="quick-payment-amount"
                    type="number" step="0.01" min="0.01"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                    autoFocus
                    required
                    className="font-['JetBrains_Mono']"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Modalità</Label>
                  <Select value={paymentForm.method} onValueChange={(v) => setPaymentForm({ ...paymentForm, method: v })}>
                    <SelectTrigger data-testid="quick-payment-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Contanti">Contanti</SelectItem>
                      <SelectItem value="Bonifico">Bonifico</SelectItem>
                      <SelectItem value="Assegno">Assegno</SelectItem>
                      <SelectItem value="Carta">Carta</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Data Pagamento</Label>
                <Input type="date" value={paymentForm.payment_date} onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })} />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Note</Label>
                <Input value={paymentForm.note} onChange={(e) => setPaymentForm({ ...paymentForm, note: e.target.value })} placeholder="Opzionale" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setPaymentDDT(null)}>Annulla</Button>
                <Button type="submit" data-testid="quick-payment-save-btn" disabled={paymentSaving} className="bg-green-600 hover:bg-green-700 text-white">
                  {paymentSaving ? 'Salvataggio…' : 'Registra Acconto'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Export ZIP Dialog */}
      <Dialog open={exportDialog} onOpenChange={setExportDialog}>
        <DialogContent data-testid="export-zip-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileArchive className="w-5 h-5" /> Scarica DDT del periodo
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-muted-foreground">
              Verrà generato un file <b>.zip</b> con tutti i PDF dei DDT del periodo selezionato (esclude bozze e cestino).
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Anno</Label>
                <Select value={String(exportYear)} onValueChange={(v) => setExportYear(parseInt(v))}>
                  <SelectTrigger data-testid="export-year-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[0, 1, 2, 3, 4].map(off => {
                      const y = currentYear - off;
                      return <SelectItem key={y} value={String(y)}>{y}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Mese</Label>
                <Select value={exportMonth} onValueChange={setExportMonth}>
                  <SelectTrigger data-testid="export-month-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutto l&apos;anno</SelectItem>
                    {['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'].map((nm, idx) => (
                      <SelectItem key={idx+1} value={String(idx+1)}>{nm}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setExportDialog(false)}>Annulla</Button>
              <Button
                data-testid="confirm-export-zip-btn"
                onClick={handleExportZip}
                disabled={exportLoading}
                className="btn-primary"
              >
                <Download className="w-4 h-4 mr-2" />
                {exportLoading ? 'Preparazione…' : 'Scarica ZIP'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DDTListPage;
